import { aiAllowed } from "@/lib/ai-access";
import {
  Brain,
  Check,
  ChevronRight,
  CodeXml,
  Eye,
  FileDiff,
  Globe,
  ListChecks,
  LogIn,
  Pencil,
  Search,
  ShieldCheck,
  SquareTerminal,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import type {
  AgentConfigOption,
  AgentEvent,
  AgentModel,
  AgentPermissionRequest,
  AgentToolCall,
} from "@ide/shared";
import { Markdown } from "@/components/markdown";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { premiumMultiplier, useAiUsage } from "@/lib/ai-usage";
import { queryClient } from "@/lib/queries";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/utils";
import { AiBlockedGate } from "@/workspace/ai-panel";
import { ProviderMark } from "@/workspace/chat-header";
import { useTree, useWs } from "@/workspace/context";
import { activeWorkspace, deleteFile, readFile, writeFile } from "@/workspace/file-ops";
import { readProjectTexts } from "@/workspace/git";
import { useWorkspace } from "@/workspace/store";
import { startSignIn, useCopilot, useCopilotQuota } from "./github-copilot";
import { ResponseActions, Thought } from "@/components/chat-parts";
import {
  ChatInput,
  ChatPicker,
  ChatWelcome,
  INTELLIGENCE_LABEL,
  InputIconButton,
  ModelPicker,
  StatusChip,
  chatContext,
  currentEditorContext,
  DiffCount,
  openPath,
  RequestBubble,
  ResponseHeader,
  SuggestedActions,
  WorkingSetBar,
  type WorkingFile,
} from "@/components/ai-chat-ui";
import { FileTypeIcon } from "@/components/file-icons";
import { MenuItem, MenuLabel, MenuSeparator } from "@/components/ui/overlay";
import {
  ChatSessionBar,
  findSession,
  revealMessage,
  sessionTitle,
  useChatHistory,
  type ChatSession,
} from "@/components/chat-sessions";

/**
 * Copilot Chat (plugin `kompil.github-copilot`): o agente oficial do GitHub Copilot pelo ACP, no
 * processo principal do app (apps/desktop/src/agent.ts). Ele trabalha num espelho local do
 * projeto; os arquivos que ele cria ou altera chegam aqui e entram no projeto (e no time) como
 * edições normais. Editar e rodar comandos passam pelos cartões de permissão.
 */
type Part =
  | { type: "text"; text: string }
  | { type: "thought"; text: string }
  | { type: "tool"; call: AgentToolCall }
  | { type: "plan"; entries: Array<{ content: string; status: string }> }
  | { type: "files"; paths: string[] }
  /** Ação que a aprovação automática permitiu (fica registrada na resposta). */
  | { type: "approved"; title: string; command?: string };

interface Message {
  role: "user" | "assistant";
  parts: Part[];
  /** Anexos que foram junto com o pedido (arquivo aberto, seleção, arquivos). */
  context?: string[];
  error?: string;
  /** Quando o pedido saiu e quando a resposta terminou (ms). */
  startedAt?: number;
  doneAt?: number;
  /** Quanto tempo o modelo pensou antes de começar a responder. */
  thinkingMs?: number;
  rating?: "up" | "down";
  /** Quem respondeu ("GPT-5 mini", "Auto · Claude Sonnet 4.5"). */
  model?: string;
}

// ───────────── Preferências (por computador) ─────────────

const AUTO_KEY = "kompil.copilot.autoApprove";

function readAuto(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Aprovação automática: depois de "Sempre permitir", o Copilot não pede permissão de novo — nem
 * nesta conversa, nem nas próximas. Dá para desligar no botão do escudo, ao lado do modelo.
 */
export const useCopilotPrefs = create<{ autoApprove: boolean; setAutoApprove(v: boolean): void }>((set) => ({
  autoApprove: readAuto(),
  setAutoApprove: (v) => {
    try {
      localStorage.setItem(AUTO_KEY, v ? "1" : "0");
    } catch {
      // sem armazenamento: vale só nesta sessão
    }
    set({ autoApprove: v });
  },
}));

interface ChatState {
  projectId: string | null;
  sessionId: string | null;
  models: AgentModel[];
  model: string | null;
  /** Nível de raciocínio e modo (do próprio Copilot). */
  config: AgentConfigOption[];
  /** Abrindo a conversa (o servidor do Copilot sobe e lê o projeto). */
  starting: boolean;
  messages: Message[];
  busy: boolean;
  signedOut: boolean;
  permissions: AgentPermissionRequest[];
  /** Conversa no histórico (null: ainda sem mensagem). */
  historyId: string | null;
  /** Aberta do histórico: o Copilot ainda não conhece estas mensagens (vão como contexto). */
  restored: boolean;
  /** Modelo automático: o Copilot (ou o Kompil) escolhe o modelo de cada pedido. */
  auto: boolean;
  /** Arquivos que o Copilot alterou e ainda não foram mantidos nem desfeitos. */
  working: WorkingFile[];
}

const AUTO_MODEL_KEY = "kompil.copilot.autoModel";
const readAutoModel = () => {
  try {
    return localStorage.getItem(AUTO_MODEL_KEY) !== "0";
  } catch {
    return true;
  }
};

const EMPTY: ChatState = {
  projectId: null,
  sessionId: null,
  models: [],
  model: null,
  config: [],
  starting: false,
  messages: [],
  busy: false,
  signedOut: false,
  permissions: [],
  historyId: null,
  restored: false,
  auto: readAutoModel(),
  working: [],
};

export const useCopilotChat = create<ChatState>(() => ({ ...EMPTY }));

/** Mensagem do erro sem o prefixo do IPC do Electron ("Error invoking remote method …: Error: "). */
function cleanError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  return raw.replace(/^Error invoking remote method '[^']+': (?:\w*Error: )?/, "");
}

/** Atualiza a última resposta do assistente. */
function patchLast(fn: (m: Message) => Message) {
  useCopilotChat.setState((s) => {
    const i = s.messages.length - 1;
    if (i < 0 || s.messages[i]!.role !== "assistant") return s;
    return { messages: [...s.messages.slice(0, i), fn(s.messages[i]!)] };
  });
}

function addPart(part: Part) {
  patchLast((m) => {
    const parts = [...m.parts];
    // O modelo parou de pensar e começou a responder: guarda quanto tempo pensou.
    const thinkingMs =
      m.thinkingMs === undefined &&
      part.type !== "thought" &&
      parts.some((p) => p.type === "thought") &&
      m.startedAt
        ? Date.now() - m.startedAt
        : m.thinkingMs;
    const last = parts[parts.length - 1];
    if ((part.type === "text" || part.type === "thought") && last?.type === part.type) {
      parts[parts.length - 1] = { ...last, text: last.text + part.text };
    } else if (part.type === "tool") {
      const i = parts.findIndex((p) => p.type === "tool" && p.call.id === part.call.id);
      if (i >= 0) {
        const prev = (parts[i] as Extract<Part, { type: "tool" }>).call;
        parts[i] = { type: "tool", call: { ...prev, ...part.call, title: part.call.title || prev.title } };
      } else parts.push(part);
    } else if (part.type === "plan") {
      const i = parts.findIndex((p) => p.type === "plan");
      if (i >= 0) parts[i] = part;
      else parts.push(part);
    } else if (part.type === "files") {
      const i = parts.findIndex((p) => p.type === "files");
      if (i >= 0) {
        const prev = parts[i] as Extract<Part, { type: "files" }>;
        parts[i] = { type: "files", paths: [...new Set([...prev.paths, ...part.paths])] };
      } else parts.push(part);
    } else parts.push(part);
    return { ...m, parts, thinkingMs };
  });
}

/** Opção do pedido de permissão que libera de vez (ou, se não houver, só desta vez). */
const allowOption = (r: AgentPermissionRequest) =>
  r.options.find((o) => o.kind === "allow_always") ?? r.options.find((o) => o.kind === "allow_once");

/** Responde sozinho a um pedido de permissão (aprovação automática ligada). */
function autoApprove(request: AgentPermissionRequest): boolean {
  if (!useCopilotPrefs.getState().autoApprove) return false;
  const option = allowOption(request);
  if (!option) return false;
  desktop?.agent.respond(request.requestId, option.id);
  addPart({ type: "approved", title: toolLabel(request.title, request.kind), command: request.command });
  return true;
}

/** Arquivo que o agente mudou no espelho: entra no projeto como edição normal. */
async function applyFile(event: Extract<AgentEvent, { type: "file" }>) {
  const ws = activeWorkspace.current;
  if (!ws || ws.detail.project.id.toLowerCase() !== event.projectId) return;
  if (!ws.session.canWrite) {
    toast.error(`O Copilot alterou ${event.path}, mas seu papel não permite editar este projeto.`);
    return;
  }
  try {
    const known = useCopilotChat.getState().working.find((f) => f.path === event.path);
    const before = known ? known.before : await readFile(ws.session, event.path).catch(() => null);
    if (event.content === null) deleteFile(ws.session, event.path);
    else await writeFile(ws.session, event.path, event.content);
    useCopilotChat.setState((s) => ({
      working: [
        ...s.working.filter((f) => f.path !== event.path),
        { path: event.path, before, after: event.content },
      ],
    }));
    addPart({ type: "files", paths: [event.path] });
  } catch (err) {
    toast.error(`Não deu para aplicar ${event.path}: ${cleanError(err)}`);
  }
}

/** Manter: as alterações ficam (some da lista). */
function keepFiles(path?: string) {
  useCopilotChat.setState((s) => ({ working: path ? s.working.filter((f) => f.path !== path) : [] }));
}

/** Desfazer: o arquivo volta ao que era antes do Copilot (ou some, se ele criou). */
async function undoFiles(path?: string) {
  const ws = activeWorkspace.current;
  if (!ws) return;
  const targets = useCopilotChat.getState().working.filter((f) => !path || f.path === path);
  for (const f of targets) {
    try {
      if (f.before === null) deleteFile(ws.session, f.path);
      else await writeFile(ws.session, f.path, f.before);
    } catch (err) {
      toast.error(`Não deu para desfazer ${f.path}: ${cleanError(err)}`);
    }
  }
  keepFiles(path);
  toast(targets.length === 1 ? `${targets[0]!.path} voltou ao que era` : "Alterações desfeitas", {
    duration: 1500,
  });
}

let listening = false;
function listen() {
  if (listening || !desktop) return;
  listening = true;
  desktop.agent.onEvent((event) => {
    const s = useCopilotChat.getState();
    if (event.type === "status") {
      if (event.state === "signed-out") useCopilotChat.setState({ signedOut: true });
      return;
    }
    if (event.type === "file") {
      void applyFile(event);
      return;
    }
    if (event.type === "permission") {
      if (event.request.sessionId !== s.sessionId) return;
      if (!autoApprove(event.request))
        useCopilotChat.setState({ permissions: [...s.permissions, event.request] });
      return;
    }
    if (event.sessionId !== s.sessionId) return;
    if (event.type === "config") {
      useCopilotChat.setState({ config: event.config });
      return;
    }
    if (event.type === "chunk")
      addPart({ type: event.kind === "message" ? "text" : "thought", text: event.text });
    else if (event.type === "tool") addPart({ type: "tool", call: event.call });
    else if (event.type === "plan") addPart({ type: "plan", entries: event.entries });
  });
}

/**
 * Contexto que vai junto do pedido: o arquivo aberto, a seleção e os arquivos anexados (o agente
 * lê o projeto no espelho; aqui só dizemos onde olhar). Devolve o texto e os rótulos dos anexos.
 */
function requestContext(): { text: string; labels: string[] } {
  const { activePath, selection } = currentEditorContext("copilot");
  const { files } = chatContext.copilot.getState();
  const labels: string[] = [];
  let text = "";
  if (activePath) {
    labels.push(
      `${activePath.split("/").pop()}${selection ? `:${selection.from}${selection.to !== selection.from ? `-${selection.to}` : ""}` : ""}`,
    );
    text += selection
      ? `[Arquivo aberto no editor: ${activePath} — seleção nas linhas ${selection.from}-${selection.to}]\n\`\`\`\n${selection.text.slice(0, 20_000)}\n\`\`\`\n\n`
      : `[Arquivo aberto no editor: ${activePath}]\n\n`;
  }
  if (files.length) {
    labels.push(...files.map((f) => f.split("/").pop()!));
    text += `[Arquivos anexados ao pedido: ${files.join(", ")}]\n\n`;
  }
  return { text, labels };
}

/** Abertura em andamento (o painel abre a sessão antes do primeiro pedido; o envio espera ela). */
let opening: { projectId: string; promise: Promise<string> } | null = null;

/** Sessão do Copilot deste projeto: a atual, a que está abrindo ou uma nova. */
function sessionFor(projectId: string): Promise<string> {
  const st = useCopilotChat.getState();
  if (st.sessionId && st.projectId === projectId) return Promise.resolve(st.sessionId);
  if (opening?.projectId === projectId) return opening.promise;
  const promise = (async () => {
    useCopilotChat.setState({ starting: true });
    try {
      const ws = activeWorkspace.current;
      if (!desktop || !ws) throw new Error("Abra o projeto no Kompil Desktop.");
      const files = await readProjectTexts(ws.session);
      const session = await desktop.agent.start("copilot", projectId, files);
      useCopilotChat.setState({
        projectId,
        sessionId: session.sessionId,
        models: session.models,
        model: session.model,
        config: session.config,
        signedOut: false,
      });
      return session.sessionId;
    } finally {
      useCopilotChat.setState({ starting: false });
      opening = null;
    }
  })();
  opening = { projectId, promise };
  return promise;
}

/**
 * Abre a conversa deste projeto já ao mostrar o chat (e depois de "Nova conversa"): o primeiro
 * pedido não espera o Copilot subir nem ler o projeto.
 */
async function ensureSession(): Promise<string | null> {
  const ws = activeWorkspace.current;
  if (!desktop || !ws) return null;
  listen();
  desktop.agent.warm?.();
  try {
    return await sessionFor(ws.detail.project.id.toLowerCase());
  } catch (err) {
    if (/auth/i.test(cleanError(err))) useCopilotChat.setState({ signedOut: true });
    return null;
  }
}

async function setConfig(id: string, value: string) {
  const { sessionId } = useCopilotChat.getState();
  if (!sessionId || !desktop) return;
  const config = await desktop.agent.setConfig(sessionId, id, value);
  if (config) useCopilotChat.setState({ config });
  else toast.error("O Copilot não aceitou essa opção.");
}

/** Modelo "Auto" do próprio Copilot, se ele oferecer. */
const nativeAuto = (models: AgentModel[]) =>
  models.find((m) => /^auto$/i.test(m.id) || /^auto\b/i.test(m.name)) ?? null;

/**
 * Modelo automático: o "Auto" do Copilot quando existe; senão o Kompil escolhe — o mais econômico
 * para perguntas curtas e o modelo padrão da sessão para pedidos maiores (código, correções).
 */
export function autoModel(text: string, models: AgentModel[], fallback: string | null): AgentModel | null {
  if (!models.length) return null;
  const native = nativeAuto(models);
  if (native) return native;
  const simple =
    text.length < 160 &&
    !/```|refator|arquitet|teste|bug|erro|implement|cri[ae]|corrig|migr|otimiz/i.test(text);
  const cheapest = [...models].sort((a, b) => premiumMultiplier(a.usage) - premiumMultiplier(b.usage))[0]!;
  if (simple) return cheapest;
  return models.find((m) => m.id === fallback) ?? cheapest;
}

/** Texto da conversa aberta do histórico, para o Copilot continuar de onde parou. */
function transcript(messages: Message[]): string {
  const lines = messages.map((m) => {
    const text = m.parts
      .filter((p): p is Extract<Part, { type: "text" }> => p.type === "text")
      .map((p) => p.text)
      .join("\n");
    return `${m.role === "user" ? "Pessoa" : "Copilot"}: ${text}`;
  });
  const joined = lines.join("\n\n");
  return joined.length > 12_000 ? `…${joined.slice(-12_000)}` : joined;
}

/** Guarda a conversa no histórico (título, modelo, mensagens). */
function saveHistory(projectId: string) {
  const st = useCopilotChat.getState();
  if (!st.messages.length) return;
  const id = st.historyId ?? crypto.randomUUID();
  const prev = findSession("copilot", id);
  const first = st.messages.find((m) => m.role === "user");
  const lastModel = [...st.messages].reverse().find((m) => m.model)?.model ?? null;
  useChatHistory.getState().save({
    id,
    provider: "copilot",
    projectId,
    title: prev?.title ?? sessionTitle((first?.parts[0] as { text?: string } | undefined)?.text ?? ""),
    createdAt: prev?.createdAt ?? Date.now(),
    updatedAt: Date.now(),
    model: lastModel,
    bookmarked: prev?.bookmarked ?? false,
    bookmarks: prev?.bookmarks ?? [],
    messages: st.messages,
  });
  if (st.historyId !== id) useCopilotChat.setState({ historyId: id });
}

async function send(text: string) {
  const ws = activeWorkspace.current;
  if (!desktop || !ws) return;
  listen();
  const projectId = ws.detail.project.id.toLowerCase();
  const before = useCopilotChat.getState();
  // Conversa do histórico: o que já foi dito vai junto (o Copilot começa uma sessão nova).
  const memory = before.restored
    ? `[Continuação de uma conversa anterior]\n${transcript(before.messages)}\n\n[Novo pedido]\n`
    : "";
  const ctx = requestContext();
  const prompt = ctx.text + memory + text;
  chatContext.copilot.getState().clear();
  useCopilotChat.setState((s) => ({
    busy: true,
    messages: [
      ...s.messages,
      { role: "user", parts: [{ type: "text", text }], context: ctx.labels },
      { role: "assistant", parts: [], startedAt: Date.now() },
    ],
  }));
  try {
    // A sessão aberta pelo painel (ou a que ainda está abrindo); o espelho recebe o projeto de agora.
    const sessionId = await sessionFor(projectId);
    await desktop.agent.sync(projectId, await readProjectTexts(ws.session));
    // Modelo de cada pedido: o escolhido, ou o automático.
    let st = useCopilotChat.getState();
    if (st.auto) {
      const pick = autoModel(text, st.models, st.model);
      if (pick && pick.id !== st.model && (await desktop.agent.setModel(sessionId, pick.id)))
        useCopilotChat.setState({ model: pick.id });
    }
    st = useCopilotChat.getState();
    const used = st.models.find((m) => m.id === st.model);
    const usedName = used?.name ?? st.model ?? "Copilot";
    patchLast((m) => ({
      ...m,
      model: st.auto && !/^auto/i.test(usedName) ? `Auto · ${usedName}` : usedName,
    }));
    useCopilotChat.setState({ restored: false });
    const res = await desktop.agent.prompt(sessionId, prompt);
    // Uso do mês neste projeto: o pedido, o consumo premium do modelo e os tokens (se vierem).
    const model = used;
    useAiUsage.getState().record(
      "copilot",
      projectId,
      {
        requests: 1,
        premium: premiumMultiplier(model?.usage),
        input: res.usage?.inputTokens,
        output: res.usage?.outputTokens,
      },
      { name: ws.detail.project.name, model: model?.name ?? st.model ?? undefined, feature: "Chat e agente" },
    );
    void queryClient.invalidateQueries({ queryKey: ["copilot", "quota"] });
  } catch (err) {
    const message = cleanError(err);
    if (/auth/i.test(message)) useCopilotChat.setState({ signedOut: true });
    patchLast((m) => ({
      ...m,
      error: /auth/i.test(message) ? "Entre na sua conta do GitHub com Copilot." : message,
    }));
  } finally {
    patchLast((m) => ({ ...m, doneAt: Date.now() }));
    useCopilotChat.setState({ busy: false, permissions: [] });
    saveHistory(projectId);
  }
}

/** Nova sessão: a conversa atual fica no histórico e o Copilot começa do zero. */
function newConversation() {
  const { sessionId, busy, auto } = useCopilotChat.getState();
  if (busy && sessionId) desktop?.agent.cancel(sessionId);
  useCopilotChat.setState({ ...EMPTY, auto });
  // A próxima sessão já fica pronta para o primeiro pedido.
  void ensureSession();
}

/** Abre uma conversa do histórico (o próximo pedido continua dela). */
function openSession(session: ChatSession) {
  const { sessionId, busy, auto } = useCopilotChat.getState();
  if (busy && sessionId) desktop?.agent.cancel(sessionId);
  useCopilotChat.setState({
    ...EMPTY,
    auto,
    historyId: session.id,
    restored: true,
    messages: (session.messages as Message[]).map((m) => ({ ...m, doneAt: m.doneAt ?? m.startedAt })),
  });
}

function setAutoModel(auto: boolean) {
  try {
    localStorage.setItem(AUTO_MODEL_KEY, auto ? "1" : "0");
  } catch {
    // sem armazenamento: vale só nesta sessão
  }
  useCopilotChat.setState({ auto });
}

/** Nome do plano do Copilot como o GitHub mostra. */
export function copilotPlanName(plan: string | null | undefined): string {
  switch (plan) {
    case "free":
      return "Copilot Free";
    case "individual":
      return "Copilot Pro";
    case "individual_pro":
      return "Copilot Pro+";
    case "business":
      return "Copilot Business";
    case "enterprise":
      return "Copilot Enterprise";
    default:
      return "Copilot";
  }
}

/** Modelos dos planos maiores do Copilot (o que aparece em "Upgrade" quando a conta não tem). */
const COPILOT_MORE_MODELS = [
  "Claude Opus 5.5",
  "Claude Sonnet 5.5",
  "GPT-6 Luna",
  "GPT-6.1 Sol",
  "HydraFusion",
  "Gemini 3.8 Flash",
  "Grok 4.7",
  "Kimi K3",
];

/** Os que a conta ainda não tem, quando dá para mudar de plano. */
export function upgradeModels(available: string[], canUpgrade: boolean): string[] {
  if (!canUpgrade) return [];
  const have = new Set(available.map((n) => n.toLowerCase()));
  return COPILOT_MORE_MODELS.filter((n) => !have.has(n.toLowerCase()));
}

const CATEGORY_HINT: Record<string, string> = {
  lightweight: "Rápido e econômico",
  versatile: "Equilíbrio para o dia a dia",
  powerful: "O mais capaz, para tarefas difíceis",
};

const PLANS_URL = "https://github.com/features/copilot/plans";
const COPILOT_SETTINGS = "https://github.com/settings/copilot";

/** Catálogo da conta (categoria, níveis de raciocínio) pelo servidor do Copilot. */
function useCopilotModels() {
  const ready = useCopilot((s) => s.status?.state === "ready");
  return useQuery({
    queryKey: ["copilot", "models"],
    queryFn: () => desktop!.copilot.models!(),
    enabled: ready && typeof desktop?.copilot.models === "function",
    staleTime: 30 * 60_000,
  });
}

const openUrl = (url: string) => (desktop ? desktop.openExternal(url) : window.open(url, "_blank"));

/** Os títulos das ferramentas do Copilot chegam em inglês: os mais comuns em português. */
const TOOL_VERBS: Array<[RegExp, string]> = [
  [/^Viewing\b/i, "Lendo"],
  [/^Reading\b/i, "Lendo"],
  [/^Editing\b/i, "Editando"],
  [/^Create file\b/i, "Criar arquivo"],
  [/^Creating\b/i, "Criando"],
  [/^Delete file\b/i, "Apagar arquivo"],
  [/^Deleting\b/i, "Apagando"],
  [/^Finding files matching\b/i, "Procurando arquivos"],
  [/^Searching(?: for)?\b/i, "Buscando"],
  [/^Listing\b/i, "Listando"],
  [/^Running\b/i, "Rodando"],
  [/^Fetching\b/i, "Buscando na web"],
  [/^apply_patch$/i, "Aplicando alterações"],
];
export function toolLabel(title: string, kind?: string): string {
  const t = title.trim();
  for (const [re, pt] of TOOL_VERBS) if (re.test(t)) return t.replace(re, pt);
  return t || kind || "Ferramenta";
}

const TOOL_ICON: Record<string, typeof Eye> = {
  read: Eye,
  edit: Pencil,
  delete: Trash2,
  move: FileDiff,
  search: Search,
  execute: SquareTerminal,
  think: Brain,
  fetch: Globe,
};

function ToolRow({ call }: { call: AgentToolCall }) {
  const [open, setOpen] = useState(false);
  const Icon = TOOL_ICON[call.kind ?? ""] ?? Wrench;
  const done = call.status === "completed";
  const failed = call.status === "failed";
  return (
    <div className="text-[12.5px]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex max-w-full items-center gap-1.5 text-left text-fg-muted hover:text-fg disabled:hover:text-fg-muted"
        disabled={!call.output}
      >
        {done ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
        ) : failed ? (
          <X className="h-3.5 w-3.5 shrink-0 text-[var(--code-error)]" />
        ) : (
          <Spinner className="h-3 w-3 shrink-0" />
        )}
        <Icon className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
        <span className="min-w-0 truncate">{toolLabel(call.title, call.kind)}</span>
        {call.paths?.length
          ? call.paths.slice(0, 3).map((p) => (
              <span
                key={p}
                role="link"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  openPath(p);
                }}
                className="flex shrink-0 items-center gap-1 rounded border border-border px-1 text-[11px] text-fg hover:bg-bg-muted"
              >
                <FileTypeIcon name={p.split("/").pop()!} className="h-3 w-3" />
                {p.split("/").pop()}
              </span>
            ))
          : null}
        {call.output ? (
          <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-90")} />
        ) : null}
      </button>
      {open && call.output ? (
        <pre className="ml-5 mt-1 max-h-48 overflow-auto rounded-md border border-border bg-bg-subtle px-2 py-1.5 font-mono text-[11px] text-fg-muted">
          {call.output}
        </pre>
      ) : null}
    </div>
  );
}

function PermissionCard({ request }: { request: AgentPermissionRequest }) {
  const answer = (optionId: string | null, always = false) => {
    desktop?.agent.respond(request.requestId, optionId);
    useCopilotChat.setState((s) => ({
      permissions: s.permissions.filter((p) => p.requestId !== request.requestId),
    }));
    if (!always) return;
    // "Sempre permitir" vale para tudo daqui em diante: os outros pedidos na fila também saem.
    useCopilotPrefs.getState().setAutoApprove(true);
    for (const other of useCopilotChat.getState().permissions) {
      const option = allowOption(other);
      if (option) desktop?.agent.respond(other.requestId, option.id);
    }
    useCopilotChat.setState({ permissions: [] });
    toast.success("O Copilot não vai pedir permissão de novo", {
      description: "Comandos e alterações rodam direto. Desligue no escudo, ao lado do modelo.",
    });
  };
  const what =
    request.kind === "execute"
      ? "rodar um comando no seu computador"
      : request.kind === "edit" || request.kind === "delete" || request.kind === "move"
        ? "alterar arquivos do projeto"
        : "fazer esta ação";
  return (
    <div className="animate-fade-up rounded-xl border border-border-strong bg-bg p-3 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.6)]">
      <p className="text-[12.5px] font-medium text-fg">O Copilot quer {what}</p>
      <p className="mt-0.5 truncate text-[11.5px] text-fg-muted" title={request.title}>
        {toolLabel(request.title, request.kind)}
      </p>
      {request.command ? (
        <pre className="mt-2 max-h-28 overflow-auto rounded-md bg-bg-muted px-2 py-1.5 font-mono text-[11.5px] text-fg">
          {request.command}
        </pre>
      ) : null}
      {request.paths?.length ? (
        <p className="mt-1.5 truncate font-mono text-[11px] text-fg-subtle">{request.paths.join(", ")}</p>
      ) : null}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {request.options.map((o) => (
          <Button
            key={o.id}
            size="sm"
            variant={o.kind === "allow_once" ? "primary" : o.kind.startsWith("allow") ? "secondary" : "ghost"}
            onClick={() => answer(o.id, o.kind === "allow_always")}
          >
            {o.kind === "allow_once"
              ? "Permitir"
              : o.kind === "allow_always"
                ? "Sempre permitir (não perguntar mais)"
                : o.kind === "reject_always"
                  ? "Nunca"
                  : o.kind === "reject_once"
                    ? "Recusar"
                    : o.name}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Fim da resposta: avaliar, copiar, favoritar, o modelo e o tempo (igual no Claude). */
function ResponseFooter({ message, index }: { message: Message; index: number }) {
  const text = message.parts
    .filter((p): p is Extract<Part, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n\n");
  const seconds =
    message.startedAt && message.doneAt
      ? Math.max(1, Math.round((message.doneAt - message.startedAt) / 1000))
      : null;
  return (
    <ResponseActions
      provider="copilot"
      sessionId={useCopilotChat.getState().historyId}
      index={index}
      text={text}
      model={message.model}
      seconds={seconds}
      rating={message.rating}
      onRate={(rating) => {
        useCopilotChat.setState((s) => ({
          messages: s.messages.map((m, i) => (i === index ? { ...m, rating } : m)),
        }));
        saveHistory(useCopilotChat.getState().projectId ?? "");
      }}
      onRetry={() => {
        const st = useCopilotChat.getState();
        if (st.busy) return;
        const asked = [...st.messages.slice(0, index)].reverse().find((m) => m.role === "user");
        const text = (asked?.parts[0] as { text?: string } | undefined)?.text;
        if (text) void send(text);
      }}
    />
  );
}

function AssistantMessage({ message, live, index }: { message: Message; live: boolean; index: number }) {
  const working = useCopilotChat((s) => s.working);
  if (!message.parts.length && !message.error) return null;
  const lastThought = message.parts.reduce((n, p, i) => (p.type === "thought" ? i : n), -1);
  return (
    <div className="min-w-0 flex-1 space-y-2">
      {message.parts.map((part, i) => {
        if (part.type === "text") return <Markdown key={i} text={part.text} />;
        if (part.type === "thought")
          return (
            <Thought
              key={i}
              text={part.text}
              live={live && i === message.parts.length - 1}
              ms={i === lastThought ? message.thinkingMs : undefined}
            />
          );
        if (part.type === "approved")
          return (
            <p key={i} className="flex items-center gap-1.5 text-[11.5px] text-fg-subtle">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate" title={part.command ?? part.title}>
                Permitido automaticamente:{" "}
                {part.command ? <code className="font-mono">{part.command}</code> : part.title}
              </span>
            </p>
          );
        if (part.type === "tool") return <ToolRow key={i} call={part.call} />;
        if (part.type === "plan")
          return (
            <div key={i} className="rounded-md border border-border p-2 text-[12px]">
              <p className="mb-1 flex items-center gap-1.5 font-medium text-fg">
                <ListChecks className="h-3.5 w-3.5" /> Tarefas
              </p>
              <ul className="space-y-0.5">
                {part.entries.map((e, j) => (
                  <li
                    key={j}
                    className={cn(
                      "flex gap-1.5",
                      e.status === "completed" ? "text-fg-subtle line-through" : "text-fg-muted",
                    )}
                  >
                    <span>{e.status === "completed" ? "✓" : e.status === "in_progress" ? "›" : "·"}</span>
                    {e.content}
                  </li>
                ))}
              </ul>
            </div>
          );
        return (
          <div key={i} className="flex flex-wrap gap-1">
            {part.paths.map((p) => {
              const w = working.find((f) => f.path === p);
              return (
                <button
                  key={p}
                  onClick={() => openPath(p)}
                  className="flex items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-2 py-1 text-[12px] text-fg hover:bg-bg-muted"
                  title={p}
                >
                  <FileTypeIcon name={p.split("/").pop()!} className="h-3.5 w-3.5" />
                  {p.split("/").pop()}
                  {w ? <DiffCount before={w.before} after={w.after} /> : null}
                </button>
              );
            })}
          </div>
        );
      })}
      {message.error ? (
        <p className="rounded-md border border-fg px-2 py-1 text-[12px]">✕ {message.error}</p>
      ) : null}
      {!live && !message.error && message.doneAt ? <ResponseFooter message={message} index={index} /> : null}
    </div>
  );
}

/** O que o Copilot está fazendo agora (abaixo da conversa, com o tempo correndo). */
function WorkingStatus({ message, starting }: { message: Message | undefined; starting: boolean }) {
  const now = useNow(500);
  const last = message?.parts.at(-1);
  const runningTool = message?.parts.findLast(
    (p): p is Extract<Part, { type: "tool" }> =>
      p.type === "tool" && p.call.status !== "completed" && p.call.status !== "failed",
  );
  const label = starting
    ? "Conectando ao Copilot…"
    : !message || !message.parts.length
      ? "Lendo o projeto e o seu pedido…"
      : runningTool
        ? `${toolLabel(runningTool.call.title, runningTool.call.kind)}…`
        : last?.type === "thought"
          ? "Raciocinando…"
          : last?.type === "plan"
            ? "Seguindo o plano…"
            : "Escrevendo a resposta…";
  const seconds = message?.startedAt ? Math.max(0, Math.floor((now - message.startedAt) / 1000)) : 0;
  return (
    <div
      className="flex items-center gap-2 px-4 pb-1 text-[12px] text-fg-muted"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inset-0 animate-ping rounded-full bg-fg-subtle opacity-50" />
        <span className="relative h-2 w-2 rounded-full bg-fg-muted" />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {seconds > 0 ? (
        <span className="shrink-0 font-mono text-[11px] text-fg-subtle">{seconds} s</span>
      ) : null}
    </div>
  );
}

// ───────────── Detalhar o pedido ─────────────

const OUTCOMES = [
  { id: "", label: "O Copilot decide" },
  { id: "explain", label: "Só explicar (sem mudar arquivos)" },
  { id: "edit", label: "Alterar o código" },
  { id: "tests", label: "Escrever testes" },
  { id: "plan", label: "Plano passo a passo antes" },
];

interface Spec {
  scope: string;
  constraints: string;
  outcome: string;
}

const EMPTY_SPEC: Spec = { scope: "", constraints: "", outcome: "" };

/** Pedido + campos → um prompt claro (o que fazer, onde, com quais regras e o que entregar). */
export function composePrompt(goal: string, spec: Spec): string {
  const lines = [goal.trim()];
  if (spec.scope.trim()) lines.push(`\nOnde: ${spec.scope.trim()}`);
  if (spec.constraints.trim()) lines.push(`Regras e restrições: ${spec.constraints.trim()}`);
  const outcome = OUTCOMES.find((o) => o.id === spec.outcome && o.id);
  if (outcome) lines.push(`O que entregar: ${outcome.label}.`);
  return lines.join("\n");
}

/** Pedido curto e sem contexto (ex.: "arruma isso"): vale sugerir os campos antes de enviar. */
export function isVague(text: string): boolean {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length < 5 && !/[./\\]\w+|`/.test(text);
}

function SpecFields({ spec, onChange, vague }: { spec: Spec; onChange: (s: Spec) => void; vague: boolean }) {
  const active = useWorkspace((s) => s.active);
  const tree = useTree();
  const openPath = active ? tree.paths.get(active) : undefined;
  const field =
    "w-full rounded-md border border-border bg-bg px-2 py-1 text-[12px] text-fg placeholder:text-fg-subtle focus:border-fg-subtle focus:outline-none";
  return (
    <div className="space-y-1.5 border-b border-border px-3 pb-2.5 pt-2.5">
      {vague ? (
        <p className="text-[11.5px] text-fg-muted">
          Pedido curto: detalhe um pouco para uma resposta melhor (tudo opcional).
        </p>
      ) : null}
      <div className="flex gap-1.5">
        <input
          value={spec.scope}
          onChange={(e) => onChange({ ...spec, scope: e.target.value })}
          placeholder="Onde? (arquivos, função, pasta)"
          className={field}
          aria-label="Onde"
        />
        {openPath && !spec.scope.includes(openPath) ? (
          <button
            type="button"
            onClick={() => onChange({ ...spec, scope: spec.scope ? `${spec.scope}, ${openPath}` : openPath })}
            className="shrink-0 rounded-md border border-border px-2 text-[11.5px] text-fg-muted hover:text-fg"
            title={`Usar ${openPath}`}
          >
            + arquivo aberto
          </button>
        ) : null}
      </div>
      <input
        value={spec.constraints}
        onChange={(e) => onChange({ ...spec, constraints: e.target.value })}
        placeholder="Regras (ex.: sem bibliotecas novas, manter a API)"
        className={field}
        aria-label="Regras e restrições"
      />
      <select
        value={spec.outcome}
        onChange={(e) => onChange({ ...spec, outcome: e.target.value })}
        className={field}
        aria-label="O que entregar"
      >
        {OUTCOMES.map((o) => (
          <option key={o.id} value={o.id}>
            {o.id ? o.label : "O que entregar? (o Copilot decide)"}
          </option>
        ))}
      </select>
    </div>
  );
}

const SUGGESTIONS = [
  "Explique como este projeto está organizado",
  "Encontre e corrija bugs no arquivo aberto",
  "Escreva testes para a função selecionada",
];

/** "Gerar instruções do agente" (como no VS Code): o Copilot cria o arquivo de instruções. */
const INSTRUCTIONS_PROMPT =
  "Analise este projeto e crie o arquivo .github/copilot-instructions.md com o que um agente precisa saber para trabalhar aqui: o objetivo do projeto, as tecnologias, como rodar e testar, as convenções de código e as pastas importantes. Seja breve e objetivo.";

const MODE_LABEL: Record<string, string> = {
  Agent: "Agente",
  Plan: "Planejar",
  Ask: "Perguntar",
  Edit: "Editar",
};
const MODE_HINT: Record<string, string> = {
  Agent: "Lê o projeto, edita arquivos e roda comandos para concluir a tarefa",
  Plan: "Monta um plano de várias etapas antes de mexer no código",
  Ask: "Só responde: não altera arquivos",
  Edit: "Altera os arquivos que você indicar",
};

/** Linha embaixo da caixa: a conta do GitHub com o plano e as permissões do agente. */
function CopilotFooter() {
  const status = useCopilot((s) => s.status);
  const quota = useCopilotQuota();
  const autoApproveOn = useCopilotPrefs((s) => s.autoApprove);
  const plan = copilotPlanName(quota.data?.plan);
  const user = status?.state === "ready" ? status.user : undefined;
  return (
    <>
      <StatusChip
        icon={<ProviderMark provider="copilot" className="h-3.5 w-3.5 shrink-0" />}
        label={plan}
        title={user ? `Conta do GitHub: ${user} · ${plan}` : plan}
      >
        <MenuLabel>{user ? `Conectado como ${user}` : "GitHub Copilot"}</MenuLabel>
        <MenuItem onSelect={() => openUrl(COPILOT_SETTINGS)}>Uso, cota e configurações da conta</MenuItem>
        {quota.data?.canUpgrade ? (
          <MenuItem onSelect={() => openUrl(PLANS_URL)}>Mudar de plano (mais modelos)</MenuItem>
        ) : null}
        <MenuSeparator />
        <MenuItem onSelect={() => void desktop?.copilot.signOut()}>Sair do Copilot</MenuItem>
      </StatusChip>
      <StatusChip
        icon={autoApproveOn ? <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> : undefined}
        label={autoApproveOn ? "Aprovação automática" : "Permissões manuais"}
        title="Como o agente pede permissão para editar arquivos e rodar comandos"
      >
        <MenuLabel>Permissões do agente</MenuLabel>
        <MenuItem
          className="h-auto items-start py-1.5"
          onSelect={() => useCopilotPrefs.getState().setAutoApprove(false)}
        >
          <Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", autoApproveOn && "opacity-0")} />
          <span>
            <span className="block">Permissões manuais</span>
            <span className="block whitespace-normal text-[11px] text-fg-subtle">
              Pergunta antes de editar arquivos e rodar comandos
            </span>
          </span>
        </MenuItem>
        <MenuItem
          className="h-auto items-start py-1.5"
          onSelect={() => useCopilotPrefs.getState().setAutoApprove(true)}
        >
          <Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", !autoApproveOn && "opacity-0")} />
          <span>
            <span className="block">Aprovação automática</span>
            <span className="block whitespace-normal text-[11px] text-fg-subtle">
              Edita e roda comandos sem perguntar (o “Sempre permitir” liga isto)
            </span>
          </span>
        </MenuItem>
      </StatusChip>
    </>
  );
}

export function CopilotChat() {
  const { detail } = useWs();
  const chat = useCopilotChat();
  const status = useCopilot((s) => s.status);
  const [input, setInput] = useState("");
  const [specOpen, setSpecOpen] = useState(false);
  const [spec, setSpec] = useState<Spec>(EMPTY_SPEC);
  const [vagueHint, setVagueHint] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const projectId = detail.project.id.toLowerCase();
  const modeOption = chat.config.find((c) => c.id === "mode");

  const catalog = useCopilotModels();
  const quota = useCopilotQuota();
  useEffect(() => listen(), []);
  const allowed = aiAllowed(detail);
  useEffect(() => {
    if (allowed && !useCopilotChat.getState().signedOut) void ensureSession();
  }, [allowed, projectId]);
  // Outro projeto: a conversa daquele projeto (o agente trabalha no espelho dele). Ao voltar ao
  // projeto, a última conversa dele reabre de onde parou.
  useEffect(() => {
    if (useCopilotChat.getState().projectId && useCopilotChat.getState().projectId !== projectId)
      newConversation();
    if (!useCopilotChat.getState().messages.length) {
      const last = useChatHistory
        .getState()
        .sessions.copilot.filter((s) => s.projectId === projectId)
        .sort((a, b) => b.updatedAt - a.updatedAt)[0];
      if (last) openSession(last);
    }
  }, [projectId]);
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [chat.messages, chat.permissions]);
  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "l") {
        if (!scroller.current?.closest("[data-chat]")?.contains(document.activeElement)) return;
        e.preventDefault();
        newConversation();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const firstUser = chat.messages.find((m) => m.role === "user");
  const header = (
    <ChatSessionBar
      provider="copilot"
      projectId={projectId}
      title={firstUser ? sessionTitle((firstUser.parts[0] as { text?: string }).text ?? "") : null}
      sessionId={chat.historyId}
      canBookmark={chat.messages.length > 0}
      onNew={newConversation}
      onOpen={(session, index) => {
        openSession(session);
        if (index !== undefined) revealMessage(scroller.current, index);
      }}
    />
  );

  if (!allowed) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <AiBlockedGate />
      </div>
    );
  }
  if (status?.state === "signed-out" || chat.signedOut) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-col items-center px-6 pt-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-bg-subtle">
            <ProviderMark provider="copilot" className="h-6 w-6" />
          </span>
          <p className="mt-4 text-[14px] font-semibold tracking-tight text-fg">Entre para usar o Copilot</p>
          <p className="mt-1.5 max-w-[260px] text-[12.5px] leading-relaxed text-fg-muted">
            Use a conta do GitHub que tem Copilot (o plano gratuito serve). É o mesmo login das sugestões no
            editor.
          </p>
          <Button
            variant="primary"
            className="mt-5"
            onClick={() => {
              useCopilotChat.setState({ signedOut: false });
              void startSignIn();
            }}
          >
            <LogIn className="h-3.5 w-3.5" /> Entrar para usar o Copilot
          </Button>
        </div>
      </div>
    );
  }

  const submit = (force = false) => {
    const text = input.trim();
    if (!text || chat.busy) return;
    // Pedido vago e campos fechados: abre os campos uma vez (dá para enviar assim mesmo).
    if (!force && !specOpen && isVague(text)) {
      setSpecOpen(true);
      setVagueHint(true);
      return;
    }
    setInput("");
    void send(specOpen ? composePrompt(text, spec) : text);
    setSpec(EMPTY_SPEC);
    setSpecOpen(false);
    setVagueHint(false);
  };
  const usedModel = chat.models.find((m) => m.id === chat.model);
  const native = nativeAuto(chat.models);
  const info = (id: string) => catalog.data?.find((c) => c.id === id);
  const pickable = chat.models.filter((m) => m !== native);
  const effortOption = chat.config.find((c) => c.id === "reasoning_effort");
  // Níveis que o modelo atual aceita (o catálogo diz; sem catálogo, os da sessão).
  const accepted = usedModel ? info(usedModel.id)?.efforts : undefined;
  const intelligence = effortOption
    ? {
        value: effortOption.current,
        options: effortOption.options
          .filter((o) => !accepted || accepted.includes(o.value) || o.value === effortOption.current)
          .map((o) => ({ value: o.value, label: INTELLIGENCE_LABEL[o.value] ?? o.name })),
        onChange: (v: string) => void setConfig("reasoning_effort", v),
      }
    : undefined;
  const isAgentMode = !modeOption || /agent/i.test(modeOption.current);

  return (
    <div className="flex h-full flex-col" data-chat="copilot">
      {header}
      <div ref={scroller} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {chat.messages.length === 0 ? (
          <ChatWelcome
            provider="copilot"
            title={isAgentMode ? "Construa com o Agente" : "Pergunte ao Copilot"}
            link={{
              label: "Gerar instruções do agente",
              hint: "para apresentar o seu código à IA.",
              onClick: () => void send(INSTRUCTIONS_PROMPT),
            }}
          />
        ) : (
          <ul className="space-y-5 px-3 py-4">
            {chat.restored ? (
              <li className="text-center text-[11.5px] text-fg-subtle">
                Conversa do histórico — o próximo pedido continua daqui.
              </li>
            ) : null}
            {chat.messages.map((m, i) => (
              <li key={i} data-message={i}>
                {m.role === "user" ? (
                  <RequestBubble text={(m.parts[0] as { text: string }).text} context={m.context} />
                ) : m.parts.length || m.error ? (
                  <div>
                    <ResponseHeader provider="copilot" />
                    <AssistantMessage
                      message={m}
                      index={i}
                      live={chat.busy && i === chat.messages.length - 1}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {chat.busy ? <WorkingStatus message={chat.messages.at(-1)} starting={chat.starting} /> : null}
      {chat.permissions.length ? (
        <div className="shrink-0 space-y-2 px-2 pb-2">
          {chat.permissions.map((p) => (
            <PermissionCard key={p.requestId} request={p} />
          ))}
        </div>
      ) : null}
      {chat.messages.length === 0 ? (
        <SuggestedActions
          items={SUGGESTIONS}
          onPick={(q) => {
            setInput(q);
            inputRef.current?.focus();
          }}
        />
      ) : null}
      <WorkingSetBar files={chat.working} onKeep={keepFiles} onUndo={(p) => void undoFiles(p)} />
      <ChatInput
        provider="copilot"
        value={input}
        onChange={setInput}
        onSubmit={() => submit()}
        onStop={() => chat.sessionId && desktop?.agent.cancel(chat.sessionId)}
        busy={chat.busy}
        inputRef={inputRef}
        placeholder={
          specOpen
            ? "O que você quer? (Enter envia com os detalhes)"
            : isAgentMode
              ? "Descreva o que construir a seguir"
              : "Pergunte ao Copilot"
        }
        above={specOpen ? <SpecFields spec={spec} onChange={setSpec} vague={vagueHint} /> : undefined}
        pickers={
          <>
            {modeOption && modeOption.options.length > 1 ? (
              <ChatPicker
                label="Modo"
                title="Modo: agente (edita e roda comandos), planejar ou só perguntar"
                value={modeOption.current}
                icon={<CodeXml className="h-3.5 w-3.5 shrink-0" />}
                options={modeOption.options.map((o) => ({
                  value: o.value,
                  label: MODE_LABEL[o.name] ?? o.name,
                  description: MODE_HINT[o.name],
                }))}
                onChange={(v) => void setConfig("mode", v)}
              />
            ) : null}
            <ModelPicker
              provider="copilot"
              source="Copilot"
              value={chat.auto ? "__auto" : (chat.model ?? "")}
              autoValue="__auto"
              autoDetail={!native && chat.auto && usedModel ? usedModel.name : undefined}
              models={pickable.map((m) => {
                const c = info(m.id);
                return {
                  value: m.id,
                  label: m.name,
                  detail: m.usage ?? (c?.multiplier !== undefined ? `${c.multiplier}x` : undefined),
                  description: c?.category ? CATEGORY_HINT[c.category] : undefined,
                };
              })}
              upgrade={upgradeModels(
                chat.models.map((m) => m.name),
                Boolean(quota.data?.canUpgrade) || quota.data?.plan === "free",
              )}
              onUpgrade={() => openUrl(PLANS_URL)}
              intelligence={intelligence}
              loading={
                chat.starting ? (
                  <span className="flex items-center gap-1.5 px-1 text-[11.5px] text-fg-subtle">
                    <Spinner className="h-3 w-3" /> Conectando ao Copilot…
                  </span>
                ) : undefined
              }
              onChange={(model) => {
                if (model === "__auto") return setAutoModel(true);
                setAutoModel(false);
                const { sessionId } = useCopilotChat.getState();
                if (!sessionId) return;
                void desktop?.agent.setModel(sessionId, model).then((ok) => {
                  if (ok) useCopilotChat.setState({ model });
                  else toast.error("O Copilot não aceitou esse modelo.");
                });
              }}
            />
          </>
        }
        footer={<CopilotFooter />}
        tools={
          <>
            {vagueHint && !chat.busy && input.trim() ? (
              <button
                type="button"
                onClick={() => submit(true)}
                className="shrink-0 rounded-md px-1.5 text-[11.5px] text-fg-muted hover:text-fg"
              >
                Enviar assim
              </button>
            ) : null}
            <InputIconButton
              label={
                specOpen ? "Fechar os detalhes do pedido" : "Detalhar o pedido (onde, regras, o que entregar)"
              }
              pressed={specOpen}
              onClick={() => {
                setSpecOpen(!specOpen);
                setVagueHint(false);
              }}
            >
              <ListChecks className="h-3.5 w-3.5" />
            </InputIconButton>
          </>
        }
      />
    </div>
  );
}
